/**
 * Block 13 PR-G Phase 4A → Phase 6.5E Item H — Section «Реквизиты договора»
 * (Preamble) на 2.13.2.
 *
 * Fields:
 *   - RegionPicker (native `<select>`, Phase 6.5E Item H — заменил read-only
 *     display из 4A; user может override region для конкретного договора)
 *   - LanguagePicker (native `<select>` — hidden if region has 1 language e.g. RU)
 *   - contractNum (text input)
 *   - contractDate (date input)
 *   - contractCity (text input)
 *
 * **Region derivation:** до 6.5E region был передан prop'ом из useAuthStore.
 * После 6.5E источник истины — `state.templateKey` (через
 * `selectContractRegion`). User overrides через RegionPicker → reducer
 * SET_CONTRACT_REGION обновляет templateKey каскадом.
 *
 * Reference: prototype `screen-2.13.2-contract.html` lines 351-394.
 */

import { useState, type Dispatch } from 'react';

import { SectionCard } from '@/features/calculator/anketa/components/SectionCard';

import { ContractNumConflictDialog } from '../components/ContractNumConflictDialog';
import { LanguagePicker } from '../components/LanguagePicker';
import { RegionPicker } from '../components/RegionPicker';
import type { ContractAction } from '../state/contractReducer';
import type { ContractFormState } from '../state/contractReducer';
import { selectContractRegion } from '../state/contractSelectors';
import { hasContractNumConflict } from '../state/savedContractsIdb';

const INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

const LABEL_CLASS = 'block text-sm font-semibold text-ink/80 mb-1';

export interface SectionPreambleProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
}

export function SectionPreamble({ state, dispatch }: SectionPreambleProps) {
  // Phase 6.5E Item H — region derived из templateKey (source of truth),
  // не из useAuthStore prop. User overrides через RegionPicker.
  const contractRegion = selectContractRegion(state);

  // Phase 7 hotfix round 7 (R7-#1, 2026-05-26) — immediate feedback на onBlur
  // contractNum input. R5-#1 save-time check остаётся как defense-in-depth.
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);

  async function handleContractNumBlur(): Promise<void> {
    const trimmed = state.contractNum.trim();
    if (trimmed === '') return;
    try {
      const conflict = await hasContractNumConflict(trimmed, state.id);
      if (conflict) setConflictDialogOpen(true);
    } catch (err) {
      if (import.meta.env.DEV) {
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[SectionPreamble] hasContractNumConflict failed', errName);
      }
      // Silent fail — save-time check в BottomActions всё равно поймает conflict.
    }
  }

  function handleConflictConfirm(): void {
    // Aibek visual verify round 12 — после dialog закрытия field очищается,
    // user перевводит unique номер.
    dispatch({ type: 'SET_FIELD', update: { path: 'contractNum', value: '' } });
    setConflictDialogOpen(false);
  }

  // Phase 6.5 #8 — убрали SectionHeader «Реквизиты договора» per Aibek decision
  // (избыточный заголовок, поля сами по себе понятны). Card structure (P-046)
  // сохраняется через SectionCard.
  return (
    <SectionCard testId="contract-section-preamble">
      <div className="flex flex-col gap-3">
        {/* Phase 6.5E Item H — Region picker (native <select>) */}
        <div data-testid="contract-region-row">
          <RegionPicker
            region={contractRegion}
            onChange={(next) => dispatch({ type: 'SET_CONTRACT_REGION', value: next })}
          />
        </div>

        <div data-testid="contract-language-row">
          <LanguagePicker
            region={contractRegion}
            templateKey={state.templateKey}
            onChange={(next) => dispatch({ type: 'SET_TEMPLATE_KEY', value: next })}
          />
        </div>

        {/* Номер + Дата */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label htmlFor="contract-num" className={LABEL_CLASS}>
              Номер
            </label>
            <input
              id="contract-num"
              type="text"
              value={state.contractNum}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'contractNum', value: e.target.value },
                })
              }
              onBlur={() => {
                void handleContractNumBlur();
              }}
              aria-label="Номер договора"
              data-testid="contract-num-input"
              className={INPUT_CLASS}
              placeholder="42"
            />
          </div>
          <div className="flex-[2]">
            <label htmlFor="contract-date" className={LABEL_CLASS}>
              Дата
            </label>
            <input
              id="contract-date"
              type="date"
              value={state.contractDate}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'contractDate', value: e.target.value },
                })
              }
              aria-label="Дата договора"
              data-testid="contract-date-input"
              className={INPUT_CLASS}
            />
          </div>
        </div>

        {/* Город */}
        <div>
          <label htmlFor="contract-city" className={LABEL_CLASS}>
            Город заключения
          </label>
          <input
            id="contract-city"
            type="text"
            value={state.contractCity}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                update: { path: 'contractCity', value: e.target.value },
              })
            }
            aria-label="Город заключения договора"
            data-testid="contract-city-input"
            className={INPUT_CLASS}
            placeholder="Астана"
          />
        </div>
      </div>
      <ContractNumConflictDialog isOpen={conflictDialogOpen} onConfirm={handleConflictConfirm} />
    </SectionCard>
  );
}
